/**
 * Font pairing suggestions — "pairs well with" partners for a family, the way design guides pair a heading face with a body face.
 *
 * Curated pairs first (well-known combinations), then category rules: a display / script / decorative face wants a quiet sans or
 * serif for body text, a sans wants a serif (and vice versa), a monospace wants a plain sans. Suggestions never repeat the
 * family itself and are limited to families the picker can load (Google Fonts) plus the classics that ship with every OS.
 */

/** Curated partners (heading ↔ body; both directions are used). */
const CURATED = {
  'Playfair Display': ['Source Sans Pro', 'Lato', 'Raleway', 'Montserrat'],
  'Montserrat': ['Merriweather', 'Lora', 'Open Sans', 'Playfair Display'],
  'Oswald': ['Open Sans', 'Lato', 'Merriweather', 'EB Garamond'],
  'Raleway': ['Merriweather', 'Lora', 'Open Sans', 'Libre Baskerville'],
  'Poppins': ['Lora', 'PT Serif', 'Inter', 'Playfair Display'],
  'Roboto': ['Lora', 'Merriweather', 'Playfair Display', 'Roboto Mono'],
  'Inter': ['Lora', 'Merriweather', 'Playfair Display', 'Source Serif Pro'],
  'Lora': ['Montserrat', 'Raleway', 'Open Sans', 'Lato'],
  'Merriweather': ['Montserrat', 'Open Sans', 'Lato', 'Oswald'],
  'Bebas Neue': ['Montserrat', 'Open Sans', 'Lato', 'Merriweather'],
  'Abril Fatface': ['Lato', 'Poppins', 'Roboto', 'Nunito'],
  'Pacifico': ['Quicksand', 'Nunito', 'Open Sans', 'Lato'],
  'Dancing Script': ['Josefin Sans', 'Lato', 'Raleway', 'EB Garamond'],
  'Great Vibes': ['Montserrat', 'Lato', 'Cormorant Garamond', 'Raleway'],
  'Lobster': ['Cabin', 'Open Sans', 'Lato', 'Merriweather'],
  'Open Sans': ['Merriweather', 'Playfair Display', 'Lora', 'Oswald'],
  'Lato': ['Merriweather', 'Playfair Display', 'Abril Fatface', 'Lora'],
  'Nunito': ['Lora', 'Playfair Display', 'PT Serif', 'Pacifico'],
  'Work Sans': ['Libre Baskerville', 'Lora', 'Spectral', 'Playfair Display'],
  'Source Sans Pro': ['Playfair Display', 'Source Serif Pro', 'Merriweather', 'Source Code Pro'],
  'Libre Baskerville': ['Montserrat', 'Raleway', 'Work Sans', 'Lato'],
  'EB Garamond': ['Montserrat', 'Oswald', 'Josefin Sans', 'Lato'],
  'Cormorant Garamond': ['Montserrat', 'Raleway', 'Great Vibes', 'Lato'],
  'Crimson Text': ['Work Sans', 'Montserrat', 'Lato', 'Raleway'],
  'PT Serif': ['PT Sans', 'Poppins', 'Nunito', 'Montserrat'],
  'PT Sans': ['PT Serif', 'Lora', 'Merriweather', 'Playfair Display'],
  'Josefin Sans': ['EB Garamond', 'Dancing Script', 'Lora', 'Playfair Display'],
  'Quicksand': ['Pacifico', 'Lora', 'Open Sans', 'Nunito'],
  'Anton': ['Open Sans', 'Lato', 'Merriweather', 'Roboto'],
  'Fjalla One': ['Lato', 'Open Sans', 'Merriweather', 'Noto Serif'],
  'Roboto Condensed': ['Roboto', 'Lora', 'Merriweather', 'Open Sans'],
  'Cabin': ['Lobster', 'Lora', 'Merriweather', 'Open Sans'],
  'Ubuntu': ['Lora', 'Merriweather', 'Ubuntu Mono', 'Open Sans'],
  'Outfit': ['Lora', 'Merriweather', 'Inter', 'Playfair Display'],
  'DM Sans': ['DM Mono', 'Lora', 'Playfair Display', 'Spectral'],
  'Space Grotesk': ['Space Mono', 'Inter', 'Lora', 'Merriweather'],
  'Spectral': ['Work Sans', 'Karla', 'Inter', 'Montserrat'],
  'Karla': ['Spectral', 'Merriweather', 'Lora', 'Playfair Display'],
  'Rubik': ['Lora', 'Merriweather', 'Inter', 'Karla'],
  'Roboto Mono': ['Roboto', 'Inter', 'Lora', 'Open Sans'],
  'Source Code Pro': ['Source Sans Pro', 'Inter', 'Source Serif Pro', 'Lato'],
  'Arial': ['Georgia', 'Times New Roman', 'Lora', 'Merriweather'],
  'Georgia': ['Arial', 'Verdana', 'Montserrat', 'Open Sans'],
  'Times New Roman': ['Arial', 'Helvetica', 'Montserrat', 'Lato'],
  'Calibri': ['Cambria', 'Georgia', 'Lora', 'Merriweather'],
  'Cambria': ['Calibri', 'Segoe UI', 'Montserrat', 'Open Sans'],
  'Verdana': ['Georgia', 'Lora', 'Merriweather', 'Playfair Display'],
  'Helvetica': ['Georgia', 'Times New Roman', 'Lora', 'Playfair Display'],
};

/** Category of each family in the picker (kept in sync with FontPicker's catalogue). */
const CATEGORY_FONTS = {
  sans: ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Nunito', 'Work Sans', 'Raleway', 'Outfit', 'DM Sans', 'Manrope', 'Rubik', 'Karla', 'Source Sans Pro', 'Fira Sans', 'Cabin', 'Hind', 'Mulish', 'Quicksand', 'Noto Sans', 'PT Sans', 'Ubuntu', 'Exo 2', 'Josefin Sans', 'Varela Round', 'Barlow', 'Archivo', 'IBM Plex Sans', 'Space Grotesk', 'Figtree', 'Lexend', 'Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Segoe UI', 'Calibri', 'Century Gothic', 'Gill Sans'],
  serif: ['Playfair Display', 'Merriweather', 'Lora', 'PT Serif', 'Libre Baskerville', 'Crimson Text', 'EB Garamond', 'Cormorant Garamond', 'Spectral', 'Bitter', 'Vollkorn', 'Source Serif Pro', 'Noto Serif', 'Amiri', 'Cardo', 'Old Standard TT', 'Neuton', 'Sorts Mill Goudy', 'Fanwood Text', 'Petrona', 'Times New Roman', 'Georgia', 'Cambria', 'Garamond', 'Palatino Linotype', 'Book Antiqua'],
  display: ['Bebas Neue', 'Righteous', 'Pacifico', 'Lobster', 'Permanent Marker', 'Alfa Slab One', 'Bungee', 'Staatliches', 'Russo One', 'Bowlby One SC', 'Bangers', 'Abril Fatface', 'Passion One', 'Fredoka One', 'Titan One', 'Rammetto One', 'Ultra', 'Shrikhand', 'Sigmar One', 'Fugaz One', 'Changa One', 'Squada One', 'Audiowide', 'Monoton', 'Rubik Moonrocks', 'Impact', 'Orbitron', 'Press Start 2P', 'VT323', 'Creepster', 'Nosifer', 'Butcherman', 'Metal Mania', 'Rubik Vinyl', 'Henny Penny', 'Flavors', 'Ewert', 'Faster One', 'Freckle Face', 'Jolly Lodger', 'Kranky', 'Lacquer', 'Luckiest Guy', 'Modak', 'Nabla', 'Rye', 'Sarina', 'Special Elite', 'Train One', 'Vampiro One'],
  script: ['Dancing Script', 'Great Vibes', 'Parisienne', 'Allura', 'Sacramento', 'Alex Brush', 'Satisfy', 'Tangerine', 'Pinyon Script', 'Marck Script', 'Rouge Script', 'Italianno', 'Yellowtail', 'Kaushan Script', 'Cookie', 'Mr De Haviland', 'Herr Von Muellerhoff', 'Qwigley', 'Berkshire Swash', 'Niconne', 'Courgette', 'Merienda', 'Caveat', 'La Belle Aurore', 'Homemade Apple', 'Indie Flower', 'Architects Daughter', 'Shadows Into Light', 'Patrick Hand', 'Gochi Hand', 'Gloria Hallelujah', 'Just Another Hand', 'Schoolbell', 'Covered By Your Grace', 'Reenie Beanie', 'Rock Salt', 'Neucha', 'Coming Soon', 'Short Stack', 'Swanky and Moo Moo', 'Handlee', 'Mali', 'Sedgwick Ave', 'Nothing You Could Do', 'Sue Ellen Francisco', 'Comic Sans MS'],
  mono: ['Roboto Mono', 'Fira Code', 'Source Code Pro', 'JetBrains Mono', 'IBM Plex Mono', 'Space Mono', 'Ubuntu Mono', 'Inconsolata', 'Cousine', 'Anonymous Pro', 'Overpass Mono', 'DM Mono', 'Azeret Mono', 'Share Tech Mono', 'Red Hat Mono', 'Courier New', 'Lucida Console'],
  condensed: ['Oswald', 'Roboto Condensed', 'Barlow Condensed', 'Pathway Gothic One', 'Yanone Kaffeesatz', 'Anton', 'Saira Condensed', 'Fjalla One', 'Chivo', 'Economica', 'Encode Sans Condensed', 'IBM Plex Sans Condensed', 'Teko', 'Pathway Extreme', 'Jost', 'Six Caps', 'Chakra Petch', 'Dosis', 'Graduate'],
};

const CATEGORY_OF = new Map();
Object.entries(CATEGORY_FONTS).forEach(([cat, list]) => list.forEach((f) => CATEGORY_OF.set(f.toLowerCase(), cat)));

/** Quiet body / heading partners per category of the *selected* font. */
const RULES = {
  sans: ['Merriweather', 'Lora', 'Playfair Display', 'EB Garamond'],
  serif: ['Montserrat', 'Open Sans', 'Lato', 'Raleway'],
  display: ['Open Sans', 'Lato', 'Montserrat', 'Lora'],
  script: ['Lato', 'Montserrat', 'Raleway', 'EB Garamond'],
  mono: ['Inter', 'Open Sans', 'Lora', 'Roboto'],
  condensed: ['Open Sans', 'Lato', 'Merriweather', 'Lora'],
  unknown: ['Inter', 'Lora', 'Montserrat', 'Merriweather'],
};

export const fontCategory = (family) => CATEGORY_OF.get(String(family || '').trim().toLowerCase()) || 'unknown';

const BODY_FRIENDLY = new Set(['sans', 'serif', 'unknown']);

/** Human hint for a suggestion: which role the pair plays next to the selected font. */
export const pairRole = (selected, partner) => {
  const a = fontCategory(selected), b = fontCategory(partner);
  if (!BODY_FRIENDLY.has(a) && BODY_FRIENDLY.has(b)) return 'body text';
  if (BODY_FRIENDLY.has(a) && !BODY_FRIENDLY.has(b)) return 'headings';
  if (a === 'sans' && b === 'serif') return 'headings or body';
  if (a === 'serif' && b === 'sans') return 'headings or body';
  return 'contrast';
};

/**
 * Up to `limit` partner families for `family`: curated pairs, then families that list it as a partner, then the category rule.
 */
export const fontPairs = (family, limit = 4) => {
  const fam = String(family || '').split(',')[0].replace(/["']/g, '').trim();
  if (!fam) return [];
  const out = [];
  const add = (f) => { if (f && f.toLowerCase() !== fam.toLowerCase() && !out.some((x) => x.toLowerCase() === f.toLowerCase())) out.push(f); };
  const key = Object.keys(CURATED).find((k) => k.toLowerCase() === fam.toLowerCase());
  (key ? CURATED[key] : []).forEach(add);
  if (out.length < limit) Object.entries(CURATED).forEach(([k, partners]) => { if (partners.some((p) => p.toLowerCase() === fam.toLowerCase())) add(k); });
  if (out.length < limit) (RULES[fontCategory(fam)] || RULES.unknown).forEach(add);
  return out.slice(0, limit);
};
